import { useCallback, useRef } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { endOfMonth, startOfMonth } from 'date-fns';
import {
  getGetVenuaPlannerCalendarTermsQueryKey,
  getVenuaPlannerCalendarTerms,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import type { GetCalendarTermsResponse } from '@/api/generated/schemas';
import { buildCalendarEvents, isoDate, type CalendarEvent } from './calendarModel';

export interface CalendarMonthsData {
  /** Termíny přes všechny načtené měsíce, klíčované `termId`. */
  eventsById: ReadonlyMap<string, CalendarEvent>;
}

const EMPTY: CalendarMonthsData = { eventsById: new Map() };

/**
 * Načte termíny pro okno měsíců — **jeden React Query dotaz na měsíc** (`from=startOfMonth,
 * to=endOfMonth`, ≤ 31 dní, pod limitem endpointu 100 dní). Každý měsíc se cachuje a načítá
 * nezávisle, takže dorůstání okna dotahuje jen chybějící měsíce a scrollování zpět je zadarmo.
 * Hook nelze volat ve smyčce → `useQueries` nad generovanými Orval funkcemi (žádný ruční fetch).
 *
 * `enabled: false` (skrytý workspace) dotazy zastaví, ale už načtené měsíce zůstávají v cache —
 * po odkrytí se kalendář vykreslí bez čekání a se stejnými výškami bloků.
 *
 * ## Referenční stabilita
 *
 * Výsledek si **drží identitu**, dokud se odpovědi opravdu nezmění. Na tom stojí výkon celé plochy:
 * z termínů se počítá rozvrh a ten se pak vykresluje do stovek řádků, takže kdyby hook vracel při
 * každém renderu nový objekt, propadlo by se to až k překreslení všech řádků — a to i při pouhém
 * posuvu, který s daty nemá nic společného. Proto:
 *
 * 1. mapování odpovědi na termíny se cachuje podle **identity odpovědi** (`WeakMap`),
 * 2. a když jsou všechny odpovědi tytéž jako minule, vrátí se rovnou minulý výsledek.
 */
export function useCalendarMonthsData(
  months: readonly Date[],
  options: { enabled?: boolean } = {}
): CalendarMonthsData {
  const enabled = options.enabled ?? true;

  const eventsCache = useRef(new WeakMap<GetCalendarTermsResponse, CalendarEvent[]>());
  const lastRef = useRef<{ sources: unknown[]; value: CalendarMonthsData } | null>(null);

  const combine = useCallback(
    (results: UseQueryResult<GetCalendarTermsResponse>[]): CalendarMonthsData => {
      const sources = results.map((result) => result.data);
      const previous = lastRef.current;
      if (
        previous &&
        previous.sources.length === sources.length &&
        sources.every((source, index) => source === previous.sources[index])
      ) {
        return previous.value;
      }

      const eventsById = new Map<string, CalendarEvent>();
      for (const source of sources) {
        if (!source) continue;
        let events = eventsCache.current.get(source);
        if (!events) {
          events = buildCalendarEvents(source);
          eventsCache.current.set(source, events);
        }
        // Hraniční termín přijde v dotazu obou sousedních měsíců — bere se ten první.
        for (const event of events) {
          if (!eventsById.has(event.termId)) eventsById.set(event.termId, event);
        }
      }

      const value: CalendarMonthsData = eventsById.size === 0 ? EMPTY : { eventsById };
      lastRef.current = { sources, value };
      return value;
    },
    []
  );

  return useQueries({
    queries: months.map((month) => {
      const params = {
        from: isoDate(startOfMonth(month)),
        to: isoDate(endOfMonth(month)),
      };
      return {
        queryKey: getGetVenuaPlannerCalendarTermsQueryKey(params),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          getVenuaPlannerCalendarTerms(params, signal),
        enabled,
      };
    }),
    combine,
  });
}
